import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Utils } from 'app/components/utils/Utils';
import { Log } from 'app/core/logging/log';
import { FieldFilter, FilterModel, OperatorFilterEnum } from 'app/models/filter-model';
import { WorkerManagerModel } from 'app/models/worker-manager-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';

@Component({
  selector: 'app-admin-worker-manager-page',
  templateUrl: './admin-worker-manager.page.html',
  styleUrls: ['./admin-worker-manager.page.scss']
})
export class WorkerManagerPage implements OnInit {
  state: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<WorkerManagerModel>([]);
  totalElements = 50;
  pageSize = 10;
  pageIndex = 0;
  fieldFilters: FieldFilter[] = [];
  displayedColumns: string[] = ['status', 'type', 'payload', 'aggregateVersion', 'attempts', 'nextRun', 'createdAt', 'action'];
  workers: {
    content: WorkerManagerModel[];
    pageNumber: number;
    totalElements: number;
    size: number;
    last: boolean;
  } = { totalElements: 50, size: 0, pageNumber: 0, last: false, content: [] };

  constructor(
    private dialog: MatDialog,
    private restService: RestService,
    public appService: AppService
  ) {}

  ngOnInit(): void {
    this.searchWorkerManager();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  onPageChange(event: PageEvent) {
    const page = event ? event.pageIndex : this.pageIndex;
    const size = event ? event.pageSize : this.pageSize;
    this.searchWorkerManager({ action: 'PAGE_CHANGE', page, size });
  }

  submit(action: string) {
    this.searchWorkerManager({ action });
  }

  setOpen(workerManager: WorkerManagerModel) {
    const dialogRef = this.dialog.open(AdminWorkerManagerDetails, {
      panelClass: 'responsive-dialog',
      data: { paymentProvider: workerManager }
    });

    dialogRef.afterClosed().subscribe((data: { updated: boolean }) => data?.updated && this.searchWorkerManager());
  }

  mapAsFilters(fieldFilters: FieldFilter[]) {
    const filters: FilterModel[] = [];
    const map: Record<string, OperatorFilterEnum> = {
      date: OperatorFilterEnum.BETWEEN,
      datetime: OperatorFilterEnum.BETWEEN,
      time: OperatorFilterEnum.BETWEEN,
      text: OperatorFilterEnum.EQUALS,
      select: OperatorFilterEnum.EQUALS,
      number: OperatorFilterEnum.EQUALS
    };

    for (const fieldFilter of fieldFilters) {
      if ((fieldFilter.value != null && fieldFilter.value !== '') || (fieldFilter.values != null && fieldFilter.values.length > 0)) {
        filters.push({
          field: fieldFilter.name,
          operator: map[fieldFilter.type],
          value: fieldFilter.value,
          values: fieldFilter.values
        });
      }
    }

    return Utils.generateFilters(filters);
  }

  searchWorkerManager({ page = this.pageIndex, size = this.pageSize, action = null }: { page?: number; size?: number; action?: string | null } = {}) {
    this.state = 'loading';
    const filters = this.mapAsFilters(this.fieldFilters);
    Log.debug('filters', { filters });
    const sort = '&sort=createdAt,desc';
    const pageSize = `&page=${page}&size=${size}`;

    this.restService.getWorkerManager(filters + sort + pageSize).subscribe(result => {
      this.state = 'loaded';
      this.workers = result;
      this.dataSource.data = result.content;
      this.totalElements = result.totalElements;
      this.pageSize = result.pageSize;
      this.pageIndex = result.pageNumber;
    });
  }

  reset(workers: WorkerManagerModel) {
    this.restService.resetWorkerManager(workers.id).subscribe(() => {
      this.searchWorkerManager();
    });
  }

  statusClass(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'RUNNING':
      case 'SUCCESS':
      case 'DONE':
        return 'wm-chip wm-chip--success';
      case 'PENDING':
      case 'SCHEDULED':
        return 'wm-chip wm-chip--warning';
      case 'FAILED':
      case 'ERROR':
        return 'wm-chip wm-chip--danger';
      default:
        return 'wm-chip wm-chip--neutral';
    }
  }

  formatEnumLabel(value?: string): string {
    if (!value) {
      return '—';
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  payloadPreview(payload?: string): string {
    const eventName = this.extractPayloadProperty(payload, 'eventName');
    if (typeof eventName === 'string' && eventName.trim()) {
      const parts = eventName.split('.');
      return parts[parts.length - 1] || eventName;
    }

    if (!payload) {
      return 'Sin payload';
    }

    return payload.length > 72 ? `${payload.slice(0, 72)}...` : payload;
  }

  formatDateTime(value?: number): string {
    if (!value) {
      return '—';
    }

    return new Date(value).toLocaleString('es-ES');
  }

  valueOrDash(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return String(value);
  }

  private extractPayloadProperty(payload: string | undefined, propertyName: string): unknown {
    if (!payload) {
      return null;
    }

    try {
      const parsed = JSON.parse(payload);
      return parsed?.[propertyName];
    } catch {
      return null;
    }
  }
}

@Component({
  selector: 'app-admin-worker-manager-details',
  templateUrl: './admin-worker-manager-details.component.html',
  styleUrls: ['./admin-worker-manager.page.scss']
})
export class AdminWorkerManagerDetails implements OnInit {
  paymentProvider: WorkerManagerModel;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { paymentProvider: WorkerManagerModel },
    public dialogRef: MatDialogRef<AdminWorkerManagerDetails>,
    private restService: RestService,
    public appService: AppService
  ) {}

  ngOnInit(): void {
    this.paymentProvider = this.data.paymentProvider;
  }

  close() {
    this.dialogRef.close({ updated: false });
  }

  statusClass(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'RUNNING':
      case 'SUCCESS':
      case 'DONE':
        return 'wm-chip wm-chip--success';
      case 'PENDING':
      case 'SCHEDULED':
        return 'wm-chip wm-chip--warning';
      case 'FAILED':
      case 'ERROR':
        return 'wm-chip wm-chip--danger';
      default:
        return 'wm-chip wm-chip--neutral';
    }
  }

  formatEnumLabel(value?: string): string {
    if (!value) {
      return '—';
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  valueOrDash(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return String(value);
  }

  formatDateTime(value?: number): string {
    if (!value) {
      return '—';
    }

    return new Date(value).toLocaleString('es-ES');
  }

  formatSeconds(value?: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (value < 60) {
      return `${value}s`;
    }

    if (value < 3600) {
      return `${Math.floor(value / 60)}m ${value % 60}s`;
    }

    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  boolLabel(value?: boolean): string {
    return value ? 'Sí' : 'No';
  }

  formatJson(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return 'Sin contenido';
    }

    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
}
